import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Actions, Card, Table, useNotify, Loader, Space, Icon, Button } from 'ebs-design';
import { Pagination } from 'components/organisms';
import { ConfirmModal } from 'components/molecules';
import { useQueryParams, useQueryUpdate } from 'hooks/useQueryParams';
import { Plus } from 'resources/icons';
import { FormModal } from '../FormModal';
import { FormNomenclatures } from '../FormNomenclatures';
import { Results, Column, FilterType, Nomenclatures } from 'types';
import { useDocumentCategoriesAPI } from 'api';
import { defaultFilters } from 'utils';
import { useRolePermission } from 'hooks/useRolePermission';
import { UserRoleCRM } from 'hooks/useFetcher/interfaces';

const formId = 'documents';

export const NomenclaturesDocuments: React.FC = () => {
  const { t } = useTranslation();
  const params = useQueryParams();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { updateQuery } = useQueryUpdate();
  const {
    getDocumentsCategory,
    getDocumentCategoryById,
    createDocumentCategory,
    updateDocumentCategory,
    deleteDocumentCategory,
  } = useDocumentCategoriesAPI();

  const [isCreate, setIsCreate] = React.useState<boolean>(false);
  const [editItem, setEditItem] = React.useState();
  const [deleteItem, setDeleteItem] = React.useState();

  const hasEditPermission = useRolePermission([UserRoleCRM.admin]);

  const { data: documents, isLoading } = useQuery<Results<Nomenclatures>>(
    ['documents', { ...defaultFilters, ...params }],
    getDocumentsCategory,
    {
      onError: () => {
        notify.error({ title: t('error.someThingIsWrong') });
      },
    },
  );

  const deleteDocumentsMutation = useMutation(deleteDocumentCategory, {
    onError: () => notify.error({ title: t('error.someThingIsWrong') }),
    onSuccess: () => {
      queryClient.invalidateQueries(['documents']);
      setDeleteItem(undefined);
      notify.success({ title: t('success.successDataDelete') });
    },
  });

  const handleDelete = (): void => {
    if (deleteItem) {
      deleteDocumentsMutation.mutate(deleteItem);
    }
  };

  const handleCancelDelete = (): void => {
    setDeleteItem(undefined);
  };

  const handleCloseModal = (): void => {
    setEditItem(undefined);
    setIsCreate(false);
  };

  const handleFilter = (type: FilterType, field: string): FilterType => {
    updateQuery({ ...params, ordering: type ? `${type === 'desc' ? '-' : ''}${field}` : type });
    return type;
  };

  const columns: Column<Nomenclatures>[] = React.useMemo(
    () => [
      {
        title: t('nomenclature.documents'),
        onFilter: (type) => handleFilter(type, 'name'),
        render: ({ name }): React.ReactNode => name,
      },
      ...(hasEditPermission
        ? [
            {
              align: 'right',
              render: ({ id }) => (
                <Space justify="end">
                  <Actions placement="bottom">
                    <Actions.Item onClick={() => setEditItem(id)}>
                      {t('nomenclature.edit')}
                    </Actions.Item>
                    <Actions.Item onClick={() => setDeleteItem(id)}>
                      {t('nomenclature.delete')}
                    </Actions.Item>
                  </Actions>
                </Space>
              ),
            } as Column<Nomenclatures>,
          ]
        : []),
    ],
    [t, documents, params],
  );

  return (
    <>
      <Space justify="space-between" className="mb-20">
        <h3 className="title">{`${t(`nomenclature.documents`)}${
          documents?.count ? ` (${documents.count})` : ``
        }`}</h3>

        {hasEditPermission && (
          <Button
            type="primary"
            prefix={<Icon component={Plus} />}
            onClick={() => setIsCreate(true)}
          >
            {t('buttons.addNew')}
          </Button>
        )}
      </Space>

      <Card>
        <Loader loading={isLoading} size="regular">
          <Table
            emptyText={t('main.noData')}
            data={documents?.results}
            columns={columns}
            className="no-border"
          />
        </Loader>
        <Card.Footer>
          <Pagination size={documents?.total_pages || 0} />
        </Card.Footer>
      </Card>

      {!!deleteItem && <ConfirmModal onSuccess={handleDelete} onCancel={handleCancelDelete} />}

      {(editItem || isCreate) && (
        <FormModal formId={formId} onCancel={handleCloseModal} isCreate={isCreate}>
          <FormNomenclatures
            formId={formId}
            getItem={getDocumentCategoryById}
            onFinish={isCreate ? createDocumentCategory : updateDocumentCategory}
            onClose={handleCloseModal}
            id={editItem}
          />
        </FormModal>
      )}
    </>
  );
};
